/**
 * Fasto Sathi — floating launcher for the in-page AI agent.
 *
 * Mounted once in the authenticated AppShell so every BI user gets it. The
 * heavy agent runtime is loaded on first click; closing the panel (its ✕
 * button) disposes the agent and brings the launcher back.
 *
 * The launcher is a small icon that can be dragged anywhere on screen; the
 * last position is remembered in localStorage.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import type { PageAgent } from 'page-agent';
import { useAuth } from '@/auth/AuthProvider';
import { cn } from '@/lib/cn';
import { isSathiConfigured } from './config';
import './sathi.css';

const POSITION_KEY = 'fasto-sathi-fab-pos';
const SIZE = 40; // matches h-10 / w-10
const MARGIN = 8;
const DRAG_THRESHOLD = 4;

type Pos = { x: number; y: number };

let warnedUnconfigured = false;

function defaultPos(): Pos {
  if (typeof window === 'undefined') return { x: 0, y: 0 };
  return {
    x: window.innerWidth - SIZE - 20,
    y: window.innerHeight - SIZE - 20,
  };
}

function clampPos(pos: Pos): Pos {
  const maxX = Math.max(MARGIN, window.innerWidth - SIZE - MARGIN);
  const maxY = Math.max(MARGIN, window.innerHeight - SIZE - MARGIN);
  return {
    x: Math.min(maxX, Math.max(MARGIN, pos.x)),
    y: Math.min(maxY, Math.max(MARGIN, pos.y)),
  };
}

function loadPos(): Pos {
  try {
    const raw = localStorage.getItem(POSITION_KEY);
    if (!raw) return defaultPos();
    const parsed = JSON.parse(raw) as Partial<Pos>;
    if (typeof parsed.x !== 'number' || typeof parsed.y !== 'number') {
      return defaultPos();
    }
    return clampPos({ x: parsed.x, y: parsed.y });
  } catch {
    return defaultPos();
  }
}

function savePos(pos: Pos) {
  try {
    localStorage.setItem(POSITION_KEY, JSON.stringify(pos));
  } catch {
    /* ignore quota / private mode */
  }
}

export function FastoSathi() {
  const { user } = useAuth();
  const agentRef = useRef<PageAgent | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState<Pos>(() =>
    typeof window === 'undefined' ? { x: 0, y: 0 } : loadPos(),
  );
  const draggingRef = useRef(false);
  const movedRef = useRef(false);
  const originRef = useRef<{ pointerX: number; pointerY: number; x: number; y: number } | null>(
    null,
  );

  // Dispose with the shell (covers logout, which unmounts AppShell).
  useEffect(
    () => () => {
      agentRef.current?.dispose();
      agentRef.current = null;
    },
    [],
  );

  // Keep the FAB on-screen when the viewport resizes.
  useEffect(() => {
    const onResize = () => setPos((p) => clampPos(p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const openSathi = useCallback(async () => {
    if (loading) return;
    if (agentRef.current) {
      agentRef.current.panel.show();
      setOpen(true);
      return;
    }
    setLoading(true);
    try {
      const { createSathiAgent } = await import('./createSathi');
      const agent = createSathiAgent(user, () => {
        agentRef.current = null;
        setOpen(false);
      });
      agentRef.current = agent;
      agent.panel.show();
      setOpen(true);
    } catch (err) {
      console.error('[FastoSathi] failed to start agent', err);
    } finally {
      setLoading(false);
    }
  }, [loading, user]);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      if (e.button !== 0) return;
      draggingRef.current = true;
      movedRef.current = false;
      originRef.current = {
        pointerX: e.clientX,
        pointerY: e.clientY,
        x: pos.x,
        y: pos.y,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [pos.x, pos.y],
  );

  const onPointerMove = useCallback((e: ReactPointerEvent<HTMLButtonElement>) => {
    if (!draggingRef.current || !originRef.current) return;
    const dx = e.clientX - originRef.current.pointerX;
    const dy = e.clientY - originRef.current.pointerY;
    if (!movedRef.current && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    movedRef.current = true;
    setPos(
      clampPos({
        x: originRef.current.x + dx,
        y: originRef.current.y + dy,
      }),
    );
  }, []);

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      const wasDrag = movedRef.current;
      originRef.current = null;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* already released */
      }
      if (wasDrag) {
        setPos((p) => {
          const next = clampPos(p);
          savePos(next);
          return next;
        });
        return;
      }
      void openSathi();
    },
    [openSathi],
  );

  if (!isSathiConfigured()) {
    if (import.meta.env.DEV && !warnedUnconfigured) {
      warnedUnconfigured = true;
      console.warn('[FastoSathi] hidden — set VITE_SATHI_LLM_MODEL to enable it.');
    }
    return null;
  }
  if (open) return null;

  return (
    <button
      type="button"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      aria-label="Open Fasto Sathi assistant"
      title="Fasto Sathi — drag to move"
      style={{ left: pos.x, top: pos.y }}
      className={cn(
        'press focus-ring fixed z-40 inline-flex h-10 w-10 items-center justify-center rounded-full',
        'cursor-grab touch-none select-none active:cursor-grabbing',
        'bg-primary text-primary-foreground',
        'shadow-[0_8px_24px_hsl(var(--primary)/0.45)] transition-transform hover:scale-[1.06]',
      )}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      ) : (
        <Sparkles className="h-4 w-4" aria-hidden />
      )}
    </button>
  );
}
